import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { SearchBar } from "./components/SearchBar";
import FileNotFoundLogo from "./components/NotFoundLogo";
import { QueryResponse } from "./components/QueryResponse";
import { FiltersForm } from "./components/FiltersForm";
import apiLibrary from "services/api";
import QuerySkeleton from "./components/QueryResponseSkeleton";
import FileLogo from "./components/NotFoundLogo";
import { FileNotFoundIcon } from "assets/icons/HeroIcons/FileNotFoundIcon";
import { FileSearchIcon } from "assets/icons/HeroIcons/FileSearchIcon";

const validationSchema = Yup.object().shape({
  creation_date_to: Yup.mixed()
    .nullable()
    .test("is-valid-date", "Invalid end date", (value) => {
      if (!value) return true;
      return moment(value, "YYYY-MM-DD", true).isValid();
    })
    .test(
      "end-after-start",
      "End date must be after start date",
      function (value) {
        const { creation_date_from } = this.parent;
        if (!value || !creation_date_from) return true;
        return moment(value).isSameOrAfter(moment(creation_date_from));
      }
    ),
  creation_date_from: Yup.mixed()
    .nullable()
    .test("is-valid-date", "Invalid start date", (value) => {
      if (!value) return true;
      return moment(value, "YYYY-MM-DD", true).isValid();
    }),
  organization: Yup.array(),
  author: Yup.array(),
  publisher: Yup.array(),
  specie: Yup.array(),
  categories: Yup.array(),
  status: Yup.array(),
  location: Yup.array(),
  message: Yup.string().required("Message is required"),
});

export default function QueryFilters() {
  const [queryResults, setQueryResults] = useState([]);
  const [originalQueryResults, setOriginalQueryResults] = useState([])
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [initialValues, setInitialValues] = useState({
    creation_date_to: "",
    creation_date_from: "",
    organization: [],
    author: [],
    publisher: [],
    specie: [],
    categories: [],
    status: [],
    location: [],
    message: "",
  });

  function reverseGeocode(
    latitude: number,
    longitude: number
  ): Promise<string> {
    const geocoder = new (window as any).google.maps.Geocoder();
    const latlng = {
      lat: parseFloat(latitude as any),
      lng: parseFloat(longitude as any),
    };

    return new Promise((resolve, reject) => {
      geocoder.geocode({ location: latlng }, (results: any, status: any) => {
        if (status === "OK" && results[0]) {
          resolve(results[0].formatted_address);
        } else {
          console.error("Geocoder failed due to:", status);
          resolve("Unknown Location");
        }
      });
    });
  }

  useEffect(() => {
    fetchChatDropdownData("organization");
    fetchChatDropdownData("author");
    fetchChatDropdownData("publisher");
    fetchChatDropdownData("specie");
    fetchChatDropdownData("location");
  }, []);
  const fetchChatDropdownData = async (entity: string) => {
    try {
      const { data } = await apiLibrary.dropdowns.getChatDropdown(entity);
      const list = data?.[entity];
      let formatted: any[] = [];

      if (entity === "specie") {
        formatted = list?.map(({ id, name }: { id: number; name: string }) => ({
          label: name,
          value: id,
        }));
      } else if (entity === "location") {
        if (Array.isArray(list)) {
          formatted = await Promise.all(
            list.map(async (item: any) => {
              try {
                // Handle both string and object formats
                const parsed = typeof item === "string" ? JSON.parse(item) : item;
  
                console.log("Parsed item:", parsed);
  
                const coords = parsed.coordinates;
  
                if (!Array.isArray(coords) || coords.length < 2) {
                  console.warn("Invalid coordinates:", parsed);
                  return null;
                }
  
                const locationData = {
                  lat: coords[1], // latitude is 2nd
                  lng: coords[0], // longitude is 1st
                };
  
                const address = await reverseGeocode(locationData.lat, locationData.lng);
  
                return {
                  label: address,
                  value: JSON.stringify(locationData),
                };
              } catch (err) {
                console.error("Error parsing location item:", item, err);
                return null;
              }
            })
          );
  
          // Remove any nulls due to failed parsing
          formatted = formatted.filter(Boolean);
        }
      } else {
        if (Array.isArray(list)) {
          formatted = list.map((item: string) => ({
            label: item,
            value: item,
          }));
        }
      }
      setInitialValues((prevState) => ({
        ...prevState,
        [entity]: formatted,
      }));
    } catch (error: any) {
      console.error(`Failed to fetch ${entity} dropdown values:`, error);
      setQueryResults([]);
    }
  };

  const handleSubmit = async (values: any) => {
    const payload = {
      ...(values.creation_date_to && {
        creation_date_to: values.creation_date_to,
      }),
      ...(values.creation_date_from && {
        creation_date_from: values.creation_date_from,
      }),
      ...(values.organization?.length > 0 && {
        organizations: values.organization
          .map((item: any) => item.value)
          .join(","),
      }),
      ...(values.author?.length > 0 && {
        authors: values.author.map((item: any) => item.value).join(","),
      }),
      ...(values.publisher?.length > 0 && {
        publishers: values.publisher.map((item: any) => item.value).join(","),
      }),
      ...(values.specie?.length > 0 && {
        specie_ids: values.specie.map((item: any) => item.value).join(","),
      }),
      ...(values.categories?.length > 0 && {
        categories: values.categories.map((item: any) => item.value).join(","),
      }),
      ...(values.status?.length > 0 && {
        statuses: values.status.map((item: any) => item.value).join(","),
      }),
      ...(values.location?.length > 0 && {
        locations: values.location.map((item: any) => item.value).join(","),
      }),
      portal_ids: queryResults.map((item: any) => item.file.id).join(","),
    };

    setLoading(true);

    try {
      const response = await apiLibrary.fileSearch.getChatFilters(payload);
      const portalIds = Array.isArray(response?.data?.portal_ids)
        ? response.data.portal_ids.map(String)
        : [];

      const filteredQueriesResult = queryResults.filter((item: any) =>
        portalIds.includes(String(item?.file?.id))
      );

    setQueryResults(filteredQueriesResult);
    } catch (error: any) {
      console.error("Failed to fetch chat results:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (message: any) => {
    const payload = {
      message: message,
    };
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await apiLibrary.fileSearch.getChatResults(payload);
      const results = response?.data.files || [];
      setQueryResults(results);
      setOriginalQueryResults(results);
    } catch (error: any) {
      setQueryResults([]);
      console.error("Failed to fetch chat results:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        handleBlur,
        resetForm,
        handleSubmit,
        isSubmitting,
      }) => {

        const handleResetFilters = () => {
          try {
            resetForm({
              values: {
                ...values, 
                creation_date_to: "",
                creation_date_from: "",
                organization: [],
                author: [],
                publisher: [],
                specie: [],
                categories: [],
                status: [],
                location: [],
              },
            });
            setQueryResults(originalQueryResults); // Reset query results to original state
          } catch (error) {
            console.error("Failed to reset filters:", error);
          }
        };

        return (
          <Form className="w-full p-0 md:p-6">
            <div className="flex gap-3 flex-col md:flex-row w-full">
              <div className="w-full md:w-[35%] pr-0 md:pr-8">
                <h2 className="text-xl font-medium text-gray-800">Filter</h2>
              </div>
              <div className="w-full md:w-[65%]">
                <SearchBar
                  searchQuery={values.message}
                  setSearchQuery={(value: string) =>
                    setFieldValue("message", value)
                  }
                  handleSearch={handleSearch}
                  error={errors.message}
                  touched={touched.message}
                  handleClearSearch={() => {
                    setFieldValue("message", "");
                    setQueryResults([]);
                    setHasSearched(false);
                  }}
                  loading={loading}
                />
              </div>
            </div>
            <div className="flex flex-col md:flex-row w-full">
              <div className="w-full md:w-[35%] pr-0 md:pr-8">
                <FiltersForm
                  values={values}
                  setFieldValue={setFieldValue}
                  handleBlur={handleBlur}
                  dropdownData={initialValues}
                  isLoading={false}
                  error={errors}
                  touched={touched}
                  isSubmitting={isSubmitting}
                  queryResults={queryResults}
                  resetForm={handleResetFilters}
                  fetchChatDropdownData={fetchChatDropdownData}
                />
              </div>
              <div className="w-full md:w-[65%]">
                {loading ? (
                  <QuerySkeleton />
                ) : !hasSearched ? (
                  <FileLogo
                    icon={<FileSearchIcon />}
                    content={<>Type your query, our system will answer you</>}
                  />
                ) : queryResults?.length === 0 ? (
                  <FileLogo
                    icon={<FileNotFoundIcon />}
                    content={<>We couldn’t find anything matching your search.</>}
                  />
                ) : (
                  <ul className="space-y-6">
                    {queryResults?.map((item) => (
                      <QueryResponse key={item} item={item} />
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </Form>
        )
      }
      }
    </Formik>
  );
}
